import { adminFetch } from "./_client";

/** One conversation summary as returned by the admin Intercom contact lookup. */
export interface IntercomConversationSummary {
  id: string;
  /** Intercom conversation state: "open" | "closed" | "snoozed". */
  state: string | null;
  open: boolean;
  /** Best-available human label — the conversation title when present. */
  title: string | null;
  /** Unix seconds — last update to the conversation. */
  updatedAt: number | null;
}

/**
 * Response of `GET /admin/intercom/contact?externalId=`.
 *
 * When Intercom isn't configured (no REST token / app id) the server answers
 * `{ configured: false }` only — every other field is absent — so callers
 * branch on `configured` first. A configured workspace with no matching
 * contact returns `configured: true` and `contact: null`.
 */
export interface IntercomContactResponse {
  configured: boolean;
  /** Public Intercom app id, used to build deep-links. Present when configured. */
  appId?: string;
  contact?: {
    id: string;
    email: string | null;
    /** Unix seconds — last time a teammate contacted this user. */
    lastContactedAt: number | null;
    /** Unix seconds — last time Intercom saw this user. */
    lastSeenAt: number | null;
  } | null;
  openConversationCount?: number;
  conversations?: IntercomConversationSummary[];
}

/**
 * Resolve a platform user (by numeric users.id passed as `externalId`) to its
 * Intercom contact + open-conversation summary. Never throws on a missing
 * Intercom config — the server returns `{ configured: false }`.
 */
export function fetchIntercomContact(token: string, externalId: string) {
  return adminFetch<IntercomContactResponse>(
    `/admin/intercom/contact?externalId=${encodeURIComponent(externalId)}`,
    token,
  );
}

/**
 * Response of `GET /admin/intercom/contact-link?externalId=` (Task #6137).
 * `{ configured: false }` — Intercom not set up (or lookup failed; treat the
 * same). `{ configured: true, found: false }` — no contact for that user.
 * `{ configured: true, found: true, url }` — deep link to the Intercom Inbox.
 */
export interface IntercomContactLinkResponse {
  configured: boolean;
  found?: boolean;
  contactId?: string;
  url?: string;
}

/**
 * Resolve a platform user (numeric users.id) to an Intercom Inbox deep link.
 * Lightweight variant of `fetchIntercomContact` for click-through actions —
 * no conversation paging. Never throws on unconfigured Intercom.
 */
export function fetchIntercomContactLink(token: string, externalId: string) {
  return adminFetch<IntercomContactLinkResponse>(
    `/admin/intercom/contact-link?externalId=${encodeURIComponent(externalId)}`,
    token,
  );
}
